import { Controller } from "@hotwired/stimulus"

// Row multi-select + floating bulk actions bar (list & spreadsheet layouts).
// Selected ids are mirrored into every bar form as hidden ids[] inputs so
// plain form submits + redirect_back keep working.
export default class extends Controller {
  static targets = ["checkbox", "groupCheckbox", "bar", "count", "form"]

  connect() {
    this.refresh()
  }

  toggle() {
    this.refresh()
  }

  // Select/deselect every row inside the closest [data-bulk-group]
  toggleGroup(event) {
    const scope = event.target.closest("[data-bulk-group]") || this.element
    scope.querySelectorAll("[data-bulk-select-target~='checkbox']").forEach((cb) => {
      cb.checked = event.target.checked
    })
    this.refresh()
  }

  clear() {
    this.checkboxTargets.forEach((cb) => (cb.checked = false))
    this.refresh()
  }

  refresh() {
    const ids = this.selectedIds()
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", ids.length === 0)
    if (ids.length > 0) this.loadBar()
    // FD-568: every count readout, not just the bar's — the bulk delete confirmation
    // dialog states the number of items it is about to destroy. FD-654: except while that
    // dialog is OPEN, for the same reason its form is skipped below — the count must keep
    // describing the ids[] the dialog will actually submit, not the row checkboxes behind
    // it. Otherwise a morph left an irreversible-delete confirmation reading "0 selected"
    // over a form still carrying N ids.
    this.countTargets.forEach((el) => {
      if (this.inOpenModal(el)) return
      el.textContent = ids.length
    })
    this.syncGroupCheckboxes()
    // FD-568 follow-up: a form inside an OPEN modal keeps the selection it was opened
    // with. A broadcast morph re-checks the row checkboxes from server markup, so
    // selectedIds() reads [] — and re-syncing that into the confirmation dialog's form
    // silently emptied a submit the user had already typed DELETE into. The dialog is a
    // snapshot of the moment it opened; the row checkboxes behind it are not its source
    // of truth while it is up. (The server also refuses an empty id list — see
    // WorkItemsController#bulk_destroy — so this is belt AND braces.)
    this.formTargets.forEach((form) => {
      if (this.inOpenModal(form)) return
      this.syncIds(form, ids)
    })
  }

  inOpenModal(el) {
    const dialog = el.closest("dialog")
    return Boolean(dialog?.open)
  }

  // FD-501: the bar's body (a form per bulk action) is fetched lazily on the
  // FIRST row selection — its forms must exist by the time any bulk action can
  // be submitted. The frame's `turbo:frame-load->bulk-select#refresh` action
  // re-syncs ids[] into the freshly-loaded forms.
  loadBar() {
    if (!this.hasBarTarget) return
    const frame = this.barTarget.querySelector("turbo-frame[data-lazy-src]:not([src])")
    if (frame) frame.setAttribute("src", frame.dataset.lazySrc)
  }

  selectedIds() {
    return this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
  }

  syncGroupCheckboxes() {
    this.groupCheckboxTargets.forEach((gc) => {
      const scope = gc.closest("[data-bulk-group]") || this.element
      const boxes = Array.from(scope.querySelectorAll("[data-bulk-select-target~='checkbox']"))
      const checked = boxes.filter((cb) => cb.checked)
      gc.checked = boxes.length > 0 && checked.length === boxes.length
      gc.indeterminate = checked.length > 0 && checked.length < boxes.length
    })
  }

  syncIds(form, ids) {
    form.querySelectorAll("input[name='ids[]']").forEach((input) => input.remove())
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "ids[]"
      input.value = id
      form.appendChild(input)
    })
  }
}
